import { withUrqlClient, NextUrqlPageContext } from "next-urql";
import { NextPageContext, NextComponentType } from "next";
import getConfig from "next/config";
import { dedupExchange, cacheExchange, fetchExchange } from "urql";
import { devtoolsExchange } from "@urql/devtools";
import { persistedFetchExchange } from "@urql/exchange-persisted-fetch";

// eslint-disable-next-line @typescript-eslint/ban-types
export type UrqlPageType<P = {}, IP = P> = NextComponentType<
  NextUrqlPageContext,
  IP,
  P
>;

interface Headers {
  [key: string]: string;
}

const { publicRuntimeConfig, serverRuntimeConfig } = getConfig();

const flattenHeader = (header: string | string[]): string =>
  Array.isArray(header) ? header.join(", ") : header;

const getFetchHeaders = (ctx: NextPageContext | undefined) => {
  const headers: Headers = {
    "x-api-key": publicRuntimeConfig.GRAPHQL_API_KEY,
    "user-agent": "Frontend/1.0 (The Atlantic)",
  };

  if (!ctx?.req) {
    return headers;
  }

  if (ctx.req.headers.cookie) {
    headers.cookie = ctx.req.headers.cookie;
  }

  if (ctx.req.headers["x-forwarded-for"]) {
    headers["x-forwarded-for"] = flattenHeader(
      ctx.req.headers["x-forwarded-for"]
    );
  }
  if (ctx.req.connection.remoteAddress) {
    const forwardedFor = (headers["x-forwarded-for"] || "").split(/,\s*?/);
    if (!forwardedFor.includes(ctx.req.connection.remoteAddress)) {
      forwardedFor.push(ctx.req.connection.remoteAddress);
    }
    headers["x-forwarded-for"] = forwardedFor.join(", ");
    headers["x-real-ip"] = forwardedFor[0] || ctx.req.connection.remoteAddress;
  }

  const referrerParts = [];

  const forwardedHost =
    ctx.req.headers["x-forwarded-host"] || ctx.req.headers.host;
  if (forwardedHost) {
    headers["x-forwarded-host"] = flattenHeader(forwardedHost);
    referrerParts.push(headers["x-forwarded-host"].split(",")[0]);
  }
  if (ctx.req.url) {
    headers["x-forwarded-path"] = ctx.req.url;
    referrerParts.push(headers["x-forwarded-path"]);
  }

  const forwardedProto = flattenHeader(
    ctx.req.headers["x-forwarded-proto"] || ""
  ).split(/,\s*?/)[0];
  const proto = forwardedProto || "http";

  if (referrerParts.length === 2) {
    headers["referer"] = `${proto}://${referrerParts.join("")}`;
  }

  if (ctx.req.headers["user-agent"]) {
    headers["x-forwarded-user-agent"] = ctx.req.headers["user-agent"];
  }

  // Set a transaction id so that we can trace errors across projects
  // https://blog.sentry.io/2019/04/04/trace-errors-through-stack-using-unique-identifiers-in-sentry
  if (ctx.req.headers["x-transaction-id"]) {
    headers["x-transaction-id"] = flattenHeader(
      ctx.req.headers["x-transaction-id"]
    );
  } else {
    const transactionId = Math.random().toString(36).substr(2, 9);
    ctx.req.headers["x-transaction-id"] = transactionId;
    headers["x-transaction-id"] = transactionId;
    if (!process.browser && serverRuntimeConfig.SENTRY_DSN) {
      import("@sentry/node").then((Sentry) =>
        Sentry.configureScope((scope) => {
          scope.setTag("transaction_id", transactionId);
        })
      );
    }
  }

  return headers;
};

export const withData = (Component: UrqlPageType, disableSsr = false) => {
  return withUrqlClient(
    (ssrExchange, ctx) => {
      const exchanges = [
        dedupExchange,
        cacheExchange,
        ssrExchange,
        persistedFetchExchange({ preferGetForPersistedQueries: true }),
        fetchExchange,
      ];

      if (process.env.NODE_ENV === "development") {
        exchanges.unshift(devtoolsExchange);
      }

      return {
        url: publicRuntimeConfig.GRAPHQL_API_URL,
        fetchOptions: {
          headers: getFetchHeaders(ctx),
          credentials: "include",
        },
        exchanges,
      };
    },
    { ssr: !disableSsr }
  )(Component);
};

export { useQuery, gql } from "urql";
export type { NextUrqlPageContext } from "next-urql";
